const { canonicalContextMap } = require("%/dynamic-component-editor/helpers/required-context-entity-type");
import App from "@bloglovin/inspire-engine/App";
import { SUBSCRIPTION_MANAGEMENT_PAGE_SLUG } from "%/dynamic-component-editor/constants/publication-constants";

// Runtime catch-up for standard pages and components. When a new
// standard surface is added in code, existing destinations are
// missing it until they next get opened — at which point this seeder
// runs against the destination's pages + components feeds, finds the
// gaps (by class-name match), and fires `CreateDynamicComponent` per
// missing entry as a blank shell.
//
// Server-side `SeedStandardSurfacesService` runs the same logic for
// the blank-create and template-clone paths; this client-side
// counterpart only fires when the destination opens AND the feeds
// have fully paginated. Without the `hasMore: false` gate the local
// dedupe index (which walks only the loaded slice) misses anything
// past the first page and the seeder would re-fire
// `CreateDynamicComponent` for every standard not yet in view —
// every page load.
//
// The canonical list lives in PHP at
// `src/Apps/CreatorStudio/StandardSurfaces.php` — the JS copy below
// must stay in lockstep (including the `providesContexts` /
// `requiredContexts` object event params the engine threads into
// rootOptions). If they drift the worst case is a missing class on
// one side gets seeded as blank by the other when the destination is
// next opened (eventually consistent).
//
// Standards use the same `type` values as user-authored pages /
// components (`"page"` / `"component"`) so they show up in
// Destination.pages and Destination.components feeds without engine
// `IN`-operator support. "Is this a standard?" is identified by the
// `standardSurfaceKey` rootOption (set at create time, never empty for
// seeded standards), not by a type prefix.
export const STANDARD_PAGES = [
  // ---- General ----
  { class: "HomePage", name: "Home Page", slug: "", providesContexts: {} },
  { class: "LoggedOutHomePage", name: "Logged Out Home Page", slug: "", providesContexts: {} },
  { class: "AboutPage", name: "About Page", slug: "about", providesContexts: {} },
  { class: "ContactUsPage", name: "Contact Us Page", slug: "contact-us", providesContexts: { contactUs: { entityType: "LegacyContactUs" } } },
  { class: "NotFoundPage", name: "Not Found Page", slug: "not-found", providesContexts: {} },
  { class: "ErrorPage", name: "Error Page", slug: "error", providesContexts: {} },
  { class: "PendingPage", name: "Pending Page", slug: "pending", providesContexts: {} },
  { class: "RejectedPage", name: "Rejected Page", slug: "rejected", providesContexts: {} },
  { class: "EventsPage", name: "Events Page", slug: "events", providesContexts: {} },
  { class: "ShopPage", name: "Shop Page", slug: "shop", providesContexts: {} },
  { class: "PendingApprovalPage", name: "Pending Approval Page", slug: "pending/approval", providesContexts: {} },
  { class: "ApplyToCollaboratePage", name: "Apply To Collaborate Page", slug: "collaborator/apply", providesContexts: { applyToCollaborate: { entityType: "LegacyApplyToCollaborate" } } },
  { class: "PendingCollaboratorPage", name: "Pending Collaborator Page", slug: "collaborator/pending", providesContexts: {} },
  { class: "LoginSignupPage", name: "Login Signup Page", slug: "publication/login", providesContexts: { loginSignup: { entityType: "LegacyLoginSignup" } } },
  { class: "WaitlistLandingPage", name: "Waitlist Landing Page", slug: "waitlist", providesContexts: {} },
  // ---- Chatroom ----
  { class: "ChatRoomPage", name: "Chat Room Page", slug: "chat-room", providesContexts: { chatRoom: { entityType: "LegacyChatRoom" } } },
  // ---- User ----
  { class: "UserProfilePage", name: "User Profile Page", slug: "user-profile", providesContexts: { userProfile: { entityType: "LegacyUserProfile" } } },
  { class: "UserSettingsPage", name: "User Settings Page", slug: "user-settings", providesContexts: { userSettings: { entityType: "LegacyUserSettings" } } },
  // ---- Subscription ----
  { class: "SubscriptionBillingHistoryPage", name: "Subscription Management Page", slug: SUBSCRIPTION_MANAGEMENT_PAGE_SLUG, providesContexts: { subscription: { entityType: "LegacySubscription" } } },
  { class: "SubscriptionFailurePage", name: "Subscription Failure Page", slug: "subscription-failure", providesContexts: { subscriptionFailure: { entityType: "LegacySubscriptionFailure" }, subscription: { entityType: "LegacySubscription" } } },
  { class: "SubscriptionSuccessPage", name: "Subscription Success Page", slug: "subscription-success", providesContexts: { subscription: { entityType: "LegacySubscription" } } },
  { class: "SubscribeNowPage", name: "Subscribe Now Page", slug: "subscribe" },
  // ---- Stories ----
  { class: "EditArticlePage", name: "Edit Article Page", slug: "edit-article/{story_id}", providesContexts: { editArticle: { entityType: "LegacyEditArticle", entitySource: "uri", entityPkUrlVars: { id: "story_id" } } } },
  { class: "CommentedStoriesPage", name: "Commented Stories Page", slug: "commented-stories", providesContexts: { commentedStories: { entityType: "LegacyCommentedStories" } } },
  { class: "LovedStoriesPage", name: "Loved Stories Page", slug: "loved-stories", providesContexts: { lovedStories: { entityType: "LegacyLovedStories" } } },
  { class: "StoryPage", name: "Story Page", slug: "story/{story_id}", providesContexts: { story: { entityType: "LegacyStory", entitySource: "uri", entityPkUrlVars: { id: "story_id" } } } },
  { class: "VotedStoriesPage", name: "Voted Stories Page", slug: "voted-stories", providesContexts: {} },
  { class: "StoriesWithHashtagPage", name: "Stories With Hashtag Page", slug: "stories-with-hashtag-page/{hashtag}", providesContexts: { storiesWithHashtag: { entityType: "LegacyStoriesWithHashtag", entitySource: "uri", entityPkUrlVars: { id: "hashtag" } } } },
  { class: "CommentPage", name: "Comment Page", slug: "comment/{message_id}", providesContexts: { comment: { entityType: "LegacyComment", entitySource: "uri", entityPkUrlVars: { id: "message_id" } } } },
  { class: "SearchPage", name: "Search Page", slug: "search", providesContexts: {} },
  // ---- Digital Products ----
  { class: "DigitalProductDetailsPage", name: "Digital Product Details Page", slug: "digital-product-details/{digital_product_id}", providesContexts: { digitalProduct: { entityType: "LegacyDigitalProduct", entitySource: "uri", entityPkUrlVars: { id: "digital_product_id" } } } },
  { class: "DigitalProductStorePage", name: "Digital Product Store Page", slug: "digital-products", providesContexts: { digitalProductStore: { entityType: "LegacyDigitalProductStore" } } },
  { class: "MyPurchasedDigitalProductStorePage", name: "My Purchased Digital Product Store Page", slug: "my-digital-products", providesContexts: { myPurchasedDigitalProducts: { entityType: "LegacyMyPurchasedDigitalProducts" } } },
  // ---- Collections ----
  { class: "CollectionsPage", name: "Collections Page", slug: "collections", providesContexts: { collections: { entityType: "LegacyCollections" } } },
  { class: "CollectionItemsPage", name: "Collection Items Page", slug: "collection-items/{collection_id}", providesContexts: { collectionItems: { entityType: "LegacyCollectionItems", entitySource: "uri", entityPkUrlVars: { id: "collection_id" } } } },
  // ---- Events ----
  // Three designable states of ONE url. They deliberately share the slug: the
  // server's EventPhase ladder picks which one answers event/{event_id}.
  { class: "EventPage", name: "Event Page", slug: "event/{event_id}", providesContexts: { event: { entityType: "LegacyEvent", entitySource: "uri", entityPkUrlVars: { id: "event_id" } } } },
  { class: "EventCountdownPage", name: "Event Countdown Page", slug: "event/{event_id}", providesContexts: { event: { entityType: "LegacyEvent", entitySource: "uri", entityPkUrlVars: { id: "event_id" } } } },
  { class: "EventEndedPage", name: "Event Ended Page", slug: "event/{event_id}", providesContexts: { event: { entityType: "LegacyEvent", entitySource: "uri", entityPkUrlVars: { id: "event_id" } } } },
  // ---- Shows ----
  { class: "ShowPage", name: "Show Page", slug: "show/{show_id}", providesContexts: { show: { entityType: "LegacyShow", entitySource: "uri", entityPkUrlVars: { id: "show_id" } } } },
  { class: "SeasonPage", name: "Season Page", slug: "show/{show_id}/season/{season_id}", providesContexts: {
    season: { entityType: "LegacySeason", entitySource: "uri", entityPkUrlVars: { id: "season_id" } },
    show: { entityType: "LegacyShow", entitySource: "uri", entityPkUrlVars: { id: "show_id" } },
  } },
  { class: "EpisodePage", name: "Episode Page", slug: "show/{show_id}/season/{season_id}/episode/{episode_id}", providesContexts: {
    episode: { entityType: "LegacyEpisode", entitySource: "uri", entityPkUrlVars: { id: "episode_id" } },
    season: { entityType: "LegacySeason", entitySource: "uri", entityPkUrlVars: { id: "season_id" } },
    show: { entityType: "LegacyShow", entitySource: "uri", entityPkUrlVars: { id: "show_id" } },
  } },
];

export const STANDARD_COMPONENTS = [
  { class: "PageHeader", name: "Page Header", requiredContexts: { userProfile: { entityType: "LegacyUserProfile" } } },
  { class: "Footer", name: "Page Footer", requiredContexts: {} },
  { class: "SubscribeToNewsletter", name: "Subscribe To Newsletter", requiredContexts: { newsletter: { entityType: "LegacyNewsletter" } } },
  { class: "SubscribeToNewsletterSubscribed", name: "Subscribe To Newsletter Subscribed", requiredContexts: {} },
  { class: "SubscribeToNewsletterSubscribedNow", name: "Subscribe To Newsletter Subscribed Now", requiredContexts: {} },
  { class: "AppBottomNavigationMenu", name: "App Bottom Navigation", requiredContexts: {} },
  { class: "UserMenuAndJoin", name: "User Menu And Join", requiredContexts: { userProfile: { entityType: "LegacyUserProfile" } } },
  { class: "UserMenuLogin", name: "User Menu Login", requiredContexts: {} },
  { class: "StoryCard", name: "Story Card", requiredContexts: { story: { entityType: "LegacyStory" } } },
  { class: "LargeStoryCard", name: "Large Story Card", requiredContexts: { story: { entityType: "LegacyStory" } } },
  { class: "Hashtag", name: "Hashtag", requiredContexts: { collection: { entityType: "LegacyCollection" }, hashtag: { entityType: "LegacyHashtag" } } },
  { class: "StoryComment", name: "Story Comment", requiredContexts: { storyComment: { entityType: "LegacyStoryComment" } } },
  { class: "UserMessage", name: "User Message", requiredContexts: { userMessage: { entityType: "LegacyUserMessage" } } },
  { class: "CommentsWidget", name: "Comments Widget", requiredContexts: { story: { entityType: "LegacyStory" } } },
  { class: "CollectionItemsCommentsWidget", name: "Collection Items Comments Widget", requiredContexts: { comment: { entityType: "LegacyComment" } } },
  { class: "DigitalProductsCommentsWidget", name: "Digital Products Comments Widget", requiredContexts: { comment: { entityType: "LegacyComment" } } },
  { class: "SubscriptionCard", name: "Subscription Card", requiredContexts: { subscriptionPlan: { entityType: "LegacySubscriptionPlan" } } },
  { class: "SubscriptionPaymentCard", name: "Subscription Payment Card", requiredContexts: { subscriptionBillingHistory: { entityType: "LegacySubscriptionBillingHistory" } } },
  { class: "EventCard", name: "Event Card", requiredContexts: { event: { entityType: "LegacyEvent" } } },
  { class: "EventCardLarge", name: "Event Card Large", requiredContexts: { event: { entityType: "LegacyEvent" } } },
  { class: "AffiliateLinkCardSmall", name: "Affiliate Link Card Small", requiredContexts: { affiliateLink: { entityType: "LegacyAffiliateLink" } } },
  { class: "AffiliateLinkCardLarge", name: "Affiliate Link Card Large", requiredContexts: { affiliateLink: { entityType: "LegacyAffiliateLink" } } },
  { class: "FollowerCard", name: "Follower Card", requiredContexts: { follower: { entityType: "LegacyFollower" } } },
  { class: "FollowerCardLarge", name: "Follower Card Large", requiredContexts: { follower: { entityType: "LegacyFollower" } } },
  { class: "SmallCollectionCard", name: "Small Collection Card", requiredContexts: { collection: { entityType: "LegacyCollection" } } },
  { class: "LargeCollectionCard", name: "Large Collection Card", requiredContexts: { collection: { entityType: "LegacyCollection" } } },
  { class: "DigitalProductCard", name: "Digital Product Card", requiredContexts: { digitalProduct: { entityType: "LegacyDigitalProduct" } } },
  { class: "DigitalProductCardLarge", name: "Digital Product Card Large", requiredContexts: { digitalProduct: { entityType: "LegacyDigitalProduct" } } },
  { class: "ServicePackageCard", name: "Service Package Card", requiredContexts: { servicePackage: { entityType: "LegacyServicePackage" } } },
  { class: "ServicePackageCardLarge", name: "Service Package Card Large", requiredContexts: { servicePackage: { entityType: "LegacyServicePackage" } } },
  { class: "ShowCardSmall", name: "Show Card Small", requiredContexts: { show: { entityType: "LegacyShow" } } },
  { class: "ShowCardLarge", name: "Show Card Large", requiredContexts: { show: { entityType: "LegacyShow" } } },
  { class: "SeasonCardSmall", name: "Season Card Small", requiredContexts: { season: { entityType: "LegacySeason" } } },
  { class: "SeasonCardLarge", name: "Season Card Large", requiredContexts: { season: { entityType: "LegacySeason" } } },
  { class: "EpisodeCardSmall", name: "Episode Card Small", requiredContexts: { episode: { entityType: "LegacyEpisode" } } },
  { class: "EpisodeCardLarge", name: "Episode Card Large", requiredContexts: { episode: { entityType: "LegacyEpisode" } } },
];

const ROOT_OPTIONS_PAGE = {
  "light-mode": { default: { default: {
    "layout-width": { r: "fill", c: "fill" },
    "layout-height": { r: "fill", c: "fill" },
    "overflow": { r: "auto", c: "auto" },
    "background-color": { r: { value: "paper", source: "theme.background" } },
  } } },
  "dark-mode": { default: { default: {
    "layout-width": { r: "fill", c: "fill" },
    "layout-height": { r: "fill", c: "fill" },
    "overflow": { r: "auto", c: "auto" },
    "background-color": { r: { value: "paper", source: "theme.background" } },
  } } },
};

const ROOT_OPTIONS_COMPONENT = {
  "light-mode": { default: { default: {
    "layout-width": { r: "fill", c: "fill" },
    "layout-height": { r: "hug", c: "hug" },
  } } },
  "dark-mode": { default: { default: {
    "layout-width": { r: "fill", c: "fill" },
    "layout-height": { r: "hug", c: "hug" },
  } } },
};

const STANDARD_PAGE_NAMES = new Set(STANDARD_PAGES.map(entry => entry.name));
const STANDARD_COMPONENT_NAMES = new Set(STANDARD_COMPONENTS.map(entry => entry.name));

const CANONICAL_NAME_BY_CLASS = new Map([...STANDARD_PAGES, ...STANDARD_COMPONENTS].map(entry => [entry.class, entry.name]));
const CLASS_BY_NAME = new Map([...STANDARD_PAGES, ...STANDARD_COMPONENTS].flatMap(entry => [
  [entry.class, entry.class],
  [entry.name, entry.class],
]));

function rootOptionsObject(rootOptions) {
  if (typeof rootOptions !== "string") {
    return rootOptions && typeof rootOptions === "object" ? rootOptions : {};
  }
  try {
    return JSON.parse(rootOptions) ?? {};
  } catch {
    return {};
  }
}

const SURFACE_KEY_SEPARATOR = "|";

function surfaceKey(name, slug) {
  return `${name}${SURFACE_KEY_SEPARATOR}${slug ?? ""}`;
}

const CLASS_BY_NAME_AND_SLUG = new Map();
STANDARD_PAGES.forEach(entry => {
  CLASS_BY_NAME_AND_SLUG.set(surfaceKey(entry.name, entry.slug), entry.class);
});
STANDARD_COMPONENTS.forEach(entry => {
  CLASS_BY_NAME_AND_SLUG.set(surfaceKey(entry.name, ""), entry.class);
});

const CLASS_BY_UNIQUE_PAGE_SLUG = new Map();
const SLUGS_CLAIMED_TWICE = new Set();
STANDARD_PAGES.forEach(entry => {
  const slug = entry.slug ?? "";
  if (CLASS_BY_UNIQUE_PAGE_SLUG.has(slug)) {
    CLASS_BY_UNIQUE_PAGE_SLUG.delete(slug);
    SLUGS_CLAIMED_TWICE.add(slug);
    return;
  }
  if (!SLUGS_CLAIMED_TWICE.has(slug)) {
    CLASS_BY_UNIQUE_PAGE_SLUG.set(slug, entry.class);
  }
});

const CONTEXTS_BY_CLASS = new Map();
[...STANDARD_PAGES, ...STANDARD_COMPONENTS].forEach(entry => {
  CONTEXTS_BY_CLASS.set(entry.class, {
    "provides-contexts": entry.providesContexts ?? {},
    "required-contexts": entry.requiredContexts ?? {},
  });
});

export default class StandardSurfaceSeeder {
  static #lazyCreateRequested = new Set();

  // Returns true when a DC name appears in the canonical standards
  // list. Used by the inspector lock gate (replaces the legacy
  // type.startsWith("standard-") check) — standards share the
  // "page" / "component" type with user-authored DCs now, so the
  // marker has to come from the name list.
  static isStandardSurfaceName(name) {
    if (typeof name !== "string" || name === "") {
      return false;
    }
    return STANDARD_PAGE_NAMES.has(name) || STANDARD_COMPONENT_NAMES.has(name);
  }

  // A built-in's identity is its stamped standard-surface-key; the name is only the fallback
  // for rows never stamped, so a rename cannot unbind it. Mirrors StandardSurfaces::standardClassOf.
  static standardClassOf({ name = "", rootOptions = null } = {}) {
    const key = rootOptionsObject(rootOptions)["standard-surface-key"];
    if (key && CANONICAL_NAME_BY_CLASS.has(key)) {
      return key;
    }
    return CLASS_BY_NAME.get(name) ?? "";
  }

  static canonicalNameForClass(standardClass) {
    return CANONICAL_NAME_BY_CLASS.get(standardClass) ?? "";
  }

  static standardSurfaceKeyForNameAndSlug(name, slug) {
    if (typeof name !== "string" || name === "") {
      return "";
    }
    return CLASS_BY_NAME_AND_SLUG.get(surfaceKey(name, slug)) ?? "";
  }

  static standardContextsForClass(standardSurfaceKey, optionKey) {
    if (typeof standardSurfaceKey !== "string" || standardSurfaceKey === "") {
      return null;
    }
    return CONTEXTS_BY_CLASS.get(standardSurfaceKey)?.[optionKey] ?? null;
  }

  static standardSurfaceKeyForSlug(slug) {
    if (typeof slug !== "string") {
      return "";
    }
    return CLASS_BY_UNIQUE_PAGE_SLUG.get(slug) ?? "";
  }

  // Public entry point. Call from a destination's editor boot after
  // its pages + components feeds have loaded. No-op if either feed
  // is still loading OR still has unseen pages (`hasMore: true`):
  // the dedupe index below walks only the locally-loaded slice, so
  // running against a partially-paginated feed would mis-identify
  // already-seeded standards as missing and re-fire 60+
  // CreateDynamicComponent events every load.
  //
  // Also a no-op for destinations created from a template — the
  // server's `/studio/destination/create-from-template` endpoint
  // owns the full seed pass for that path (clone-from-template +
  // SeedStandardSurfacesService::seed in one transaction). Letting
  // the client seeder also fire there causes duplicates: the
  // destination is reachable in the sidebar BEFORE the server's
  // deep-clone POST finishes, so the feeds load empty, the seeder
  // creates 78 blanks, and then the server's cloned + seeded DCs
  // sync down on top.
  static ensureSeededForDestination(destinationId) {
    if (destinationId === null || destinationId === undefined || destinationId === "") {
      return;
    }
    const destKey = String(destinationId);
    const studio = App.get("CreatorStudio");
    const sourceTemplate = studio.getEntityField("Destination", { id: destKey }, "sourceTemplate");
    if (sourceTemplate) {
      return;
    }
    if (!this.#isFeedSettled(studio, destKey, "pages") || !this.#isFeedSettled(studio, destKey, "components")) {
      return;
    }
    const existing = this.#indexExistingByName(studio, destKey);
    for (const entry of STANDARD_PAGES) {
      this.#seedIfMissing(studio, destKey, entry, "page", existing);
    }
    for (const entry of STANDARD_COMPONENTS) {
      this.#seedIfMissing(studio, destKey, entry, "component", existing);
    }
  }

  // A feed is "settled" for the seeder's purposes when it has loaded
  // at least once AND has no unseen pages remaining. Either condition
  // alone leaves the local index incomplete and would let the seeder
  // duplicate surfaces it just hasn't paged to yet. FeedStore exposes
  // `hasMoreNext` (next-direction pagination); there is no aggregate
  // `hasMore` meta — reading that returns undefined and `=== false`
  // would never match, so the seeder would silently never fire.
  static #isFeedSettled(studio, destKey, fieldName) {
    const hasLoaded = Boolean(studio.getFeedMeta(
      "Destination",
      { id: destKey },
      fieldName,
      "hasLoadedAtLeastOnce",
    ));
    if (!hasLoaded) {
      return false;
    }
    const hasMoreNext = studio.getFeedMeta(
      "Destination",
      { id: destKey },
      fieldName,
      "hasMoreNext",
    );
    return hasMoreNext === false;
  }

  static #indexExistingByName(studio, destKey) {
    const index = new Set();
    for (const fieldName of ["pages", "components"]) {
      const items = studio.getFeedItems("Destination", { id: destKey }, fieldName) ?? [];
      for (const item of items) {
        const id = item?.entityPk?.id;
        if (!id) {
          continue;
        }
        const name = studio.getEntityField("DynamicComponent", { id }, "name");
        if (typeof name === "string" && name !== "") {
          index.add(name);
        }
        const rootOptions = studio.getEntityField("DynamicComponent", { id }, "rootOptions");
        const standardClass = this.standardClassOf({ name, rootOptions });
        if (standardClass) {
          index.add(this.canonicalNameForClass(standardClass));
        }
      }
    }
    return index;
  }

  static #seedIfMissing(studio, destKey, entry, type, existing) {
    if (existing.has(entry.name)) {
      return;
    }
    const dedupeKey = `${destKey}|${entry.class}`;
    if (this.#lazyCreateRequested.has(dedupeKey)) {
      return;
    }
    this.#lazyCreateRequested.add(dedupeKey);
    // XXX: Canonicalise on the way out. The literals in this file still spell
    // XXX: entityType; every write persists the engine's `{entity}` shape so the
    // XXX: seeder and the inspector's save path stop producing two shapes for
    // XXX: the same fact. See helpers/required-context-entity-type.js.
    const providesContexts = canonicalContextMap(entry.providesContexts ?? {});
    const requiredContexts = canonicalContextMap(entry.requiredContexts ?? {});
    const rootOptions = type === "page" ? ROOT_OPTIONS_PAGE : ROOT_OPTIONS_COMPONENT;
    studio.triggerEvent("CreateDynamicComponent", {
      destination: { id: destKey },
      type,
      name: entry.name,
      description: "",
      slug: entry.slug ?? "",
      variantGroupId: "",
      rootElementOptions: JSON.stringify(rootOptions),
      providesContexts,
      requiredContexts,
      isStandard: true,
      standardSurfaceKey: entry.class,
    });
  }
}
